/**
 * Passive element picker. Renders only the visual layer (hover highlight
 * + already-picked highlights) and dispatches Shift+click events. The
 * parent (`QuickReportOverlay`) owns the picked-elements state, the
 * minimized-drawer instruction bar, and Enter-to-finish handling — both
 * the picker and the recording flow share that minimized-drawer pattern,
 * so it lives in one place.
 *
 * Behavior:
 *   - Move mouse: hover overlay tracks the meaningful target under the
 *     cursor (white tint always; gold border + label when Shift is held).
 *   - Shift+click: append the target to picks (calls `onAddPick`).
 *     Multi-select is the default — every shift+click adds one.
 *   - Esc: cancel (calls `onCancel`).
 *   - Other clicks: pass through unmodified.
 *
 * The parent listens for Enter and for the drawer-bar click to finish.
 */

import React from 'react';

const REGION_ATTR = 'data-feedback-region';

const INTERACTIVE_TAGS = new Set(['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA']);
const INTERACTIVE_ROLES = new Set(['button', 'tab', 'menuitem', 'link', 'checkbox', 'switch', 'option']);

const isHugeBbox = (el) => {
  if (!el || typeof el.getBoundingClientRect !== 'function') return false;
  const r = el.getBoundingClientRect();
  const vw = window.innerWidth || 1;
  const vh = window.innerHeight || 1;
  return (r.width * r.height) > (vw * vh * 0.7);
};

const isMeaningful = (el) => {
  if (!el || el === document.body || el === document.documentElement) return false;
  if (el.hasAttribute && el.hasAttribute(REGION_ATTR)) return true;
  const tag = (el.tagName || '').toUpperCase();
  if (INTERACTIVE_TAGS.has(tag)) return true;
  const role = el.getAttribute && el.getAttribute('role');
  if (role && INTERACTIVE_ROLES.has(role)) return true;
  return false;
};

const findTargetElement = (el) => {
  if (!el) return el;
  if (isMeaningful(el) && !isHugeBbox(el)) return el;
  let cur = el.parentElement;
  let depth = 0;
  while (cur && depth < 4 && cur !== document.body) {
    if (isMeaningful(cur) && !isHugeBbox(cur)) return cur;
    cur = cur.parentElement;
    depth++;
  }
  return el;
};

const buildCssPath = (el) => {
  const parts = [];
  let cur = el;
  let depth = 0;
  while (cur && cur !== document.body && depth < 4) {
    const tag = (cur.tagName || '').toLowerCase();
    const id = cur.id ? `#${cur.id}` : '';
    const cls = (cur.className && typeof cur.className === 'string')
      ? cur.className.split(/\s+/).filter(Boolean).slice(0, 2).map(c => `.${c}`).join('')
      : '';
    parts.unshift(`${tag}${id}${cls}`);
    if (id) break;
    cur = cur.parentElement;
    depth++;
  }
  return parts.join(' > ');
};

const summarize = (el) => {
  if (!el) return null;
  const region = el.getAttribute && el.getAttribute(REGION_ATTR);
  const testid = el.getAttribute && (el.getAttribute('data-testid') || el.getAttribute('data-test'));
  const id = el.id || null;
  const role = el.getAttribute && el.getAttribute('role');
  const ariaLabel = el.getAttribute && el.getAttribute('aria-label');
  const text = (el.textContent || '').trim().slice(0, 60);
  const rect = el.getBoundingClientRect ? el.getBoundingClientRect() : null;
  return {
    regionTag: region || null,
    testId: testid || null,
    elementId: id,
    role: role || null,
    ariaLabel: ariaLabel || null,
    text: text || null,
    cssPath: buildCssPath(el),
    bbox: rect ? { x: Math.round(rect.x), y: Math.round(rect.y), w: Math.round(rect.width), h: Math.round(rect.height) } : null,
  };
};

export default function ElementPicker({ pickedElements, onTogglePick, onCancel }) {
  const [hoverRect, setHoverRect] = React.useState(null);
  const [hoverLabel, setHoverLabel] = React.useState(null);
  const [hoverIsPicked, setHoverIsPicked] = React.useState(false);
  const [shiftHeld, setShiftHeld] = React.useState(false);
  const hoverElRef = React.useRef(null);

  // Identity check: an element is "the same" as a picked one when their
  // CSS paths match. cssPath is stable across viewport scroll (unlike
  // bbox), and tighter than tagName alone.
  const isSamePick = React.useCallback((summary, picked) => {
    if (!summary || !picked) return false;
    if (picked.cssPath && summary.cssPath) return picked.cssPath === summary.cssPath;
    if (picked.elementId && summary.elementId) return picked.elementId === summary.elementId;
    return false;
  }, []);
  const findExistingPickIndex = React.useCallback((summary) => {
    return (pickedElements || []).findIndex(p => isSamePick(summary, p));
  }, [pickedElements, isSamePick]);

  React.useEffect(() => {
    const onMove = (e) => {
      const target = document.elementFromPoint(e.clientX, e.clientY);
      if (!target) {
        hoverElRef.current = null;
        setHoverRect(null);
        return;
      }
      // Skip the picker's own UI surface AND the bug-report drawer/button.
      // Hovering over the minimized recording bar shouldn't grab the bar
      // itself as a candidate.
      if (target.closest && (
        target.closest('[data-feedback-picker-ui]')
        || target.closest('[data-bug-report-drawer]')
        || target.closest('[data-bug-report-button]')
      )) {
        return;
      }
      const meaningful = findTargetElement(target);
      hoverElRef.current = meaningful;
      const rect = meaningful.getBoundingClientRect();
      setHoverRect({ x: rect.x, y: rect.y, w: rect.width, h: rect.height });
      const label = (meaningful.getAttribute && (
        meaningful.getAttribute(REGION_ATTR)
        || meaningful.getAttribute('data-testid')
        || meaningful.id
        || meaningful.getAttribute('aria-label')
        || meaningful.getAttribute('role')
      )) || (meaningful.tagName ? meaningful.tagName.toLowerCase() : 'element');
      setHoverLabel(label);
      // Check if this hovered element is already in the picked list so
      // the hover overlay can render in deselect-style (red) instead of
      // add-style (gold).
      setHoverIsPicked(findExistingPickIndex(summarize(meaningful)) >= 0);
    };

    const onKeyDown = (e) => {
      if (e.key === 'Shift') setShiftHeld(true);
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel?.();
      }
    };
    const onKeyUp = (e) => {
      if (e.key === 'Shift') setShiftHeld(false);
    };

    // Capture-phase click listener. Only acts when Shift is held; lets
    // shift-less clicks bubble normally so the user can keep navigating.
    // Toggle semantics: shift+click on a NEW element adds it to the picks;
    // shift+click on an ALREADY-picked element removes it. The parent's
    // `onTogglePick` callback handles add/remove via the same identity
    // check (cssPath / elementId).
    const onClick = (e) => {
      if (!e.shiftKey) return;
      if (e.target.closest && (
        e.target.closest('[data-feedback-picker-ui]')
        || e.target.closest('[data-bug-report-drawer]')
        || e.target.closest('[data-bug-report-button]')
      )) return;
      const el = hoverElRef.current || document.elementFromPoint(e.clientX, e.clientY);
      if (!el) return;
      const meaningful = findTargetElement(el);
      const summary = summarize(meaningful);
      if (!summary) return;
      e.preventDefault();
      e.stopPropagation();
      onTogglePick?.(summary);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('click', onClick, true);
    };
  }, [onTogglePick, onCancel, findExistingPickIndex]);

  const picked = pickedElements || [];

  return (
    <>
      {/* Hover highlight — gold for "would add", red for "would deselect"
          (already in pickedElements). Both states only appear while Shift
          is held so the user can see the action they're about to take. */}
      {hoverRect && (
        <div
          data-feedback-picker-ui
          style={{
            position: 'fixed',
            top: hoverRect.y,
            left: hoverRect.x,
            width: hoverRect.w,
            height: hoverRect.h,
            background: shiftHeld
              ? (hoverIsPicked ? 'rgba(255,105,97,0.18)' : 'rgba(255,255,255,0.18)')
              : 'rgba(255,255,255,0.08)',
            border: shiftHeld
              ? (hoverIsPicked ? '2px solid #ff6961' : '2px solid #c9a227')
              : '1px solid rgba(255,255,255,0.4)',
            pointerEvents: 'none',
            zIndex: 99998,
            transition: 'background 60ms linear, border 60ms linear',
          }}
        >
          {shiftHeld && hoverLabel && (
            <div style={{
              position: 'absolute',
              top: -22,
              left: 0,
              background: hoverIsPicked ? '#ff6961' : '#c9a227',
              color: hoverIsPicked ? '#fff' : '#1a1008',
              padding: '2px 6px',
              fontSize: '0.65rem',
              borderRadius: 3,
              fontFamily: 'monospace',
              whiteSpace: 'nowrap',
            }}>
              {hoverIsPicked ? `✕ deselect ${hoverLabel}` : hoverLabel}
            </div>
          )}
        </div>
      )}

      {/* Already-picked highlights (green) */}
      {picked.map((p, i) => p.bbox && (
        <div
          key={i}
          data-feedback-picker-ui
          style={{
            position: 'fixed',
            top: p.bbox.y, left: p.bbox.x, width: p.bbox.w, height: p.bbox.h,
            border: '2px solid #27c95a',
            background: 'rgba(39,201,90,0.12)',
            pointerEvents: 'none',
            zIndex: 99998,
          }}
        />
      ))}
    </>
  );
}
